import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, User, Phone, MapPin, HelpCircle, Briefcase } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const Drivers = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session = getSessionData();

  const [drivers,       setDrivers]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [showForm,      setShowForm]      = useState(false);
  const [editData,      setEditData]      = useState(null);
  const [searchTerm,    setSearchTerm]    = useState('');
  const [statusFilter,  setStatusFilter]  = useState('all');
  const [typeFilter,    setTypeFilter]    = useState('all');
  const [showHelp,      setShowHelp]      = useState(false);

  // Works modal state
  const [showWorks,     setShowWorks]     = useState(false);
  const [worksDriver,   setWorksDriver]   = useState(null);
  const [worksData,     setWorksData]     = useState([]);
  const [worksLoading,  setWorksLoading]  = useState(false);
  const [worksError,    setWorksError]    = useState('');

  const [formData, setFormData] = useState({
    driverid: '', drivername: '', address1: '', address2: '', address3: '',
    phone: '', phone1: '', driverstatus: 'H', isactive: 1, isemployee: 1,
  });

  useEffect(() => { loadDrivers(); }, []);

  const loadDrivers = async () => {
    setLoading(true); setError('');
    try {
      const res = await apiClient.get('/Drivers.php');
      if (res.data.success) setDrivers(res.data.data || []);
      else setError(res.data.message || 'Failed to load drivers');
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        setTimeout(() => window.location.href = '/login', 2000);
      } else { setError(err.response?.data?.message || 'Failed to load drivers'); }
    } finally { setLoading(false); }
  };

  const handleNew = () => {
    setEditData(null);
    setFormData({ driverid: '', drivername: '', address1: '', address2: '', address3: '', phone: '', phone1: '', driverstatus: 'H', isactive: 1, isemployee: 1 });
    setShowForm(true);
  };

  const handleEdit = (driver) => {
    setEditData(driver);
    setFormData({
      driverid: driver.driverid, drivername: driver.drivername || '',
      address1: driver.address1 || '', address2: driver.address2 || '', address3: driver.address3 || '',
      phone: driver.phone || '', phone1: driver.phone1 || '',
      driverstatus: driver.driverstatus || 'H',
      isactive: parseInt(driver.isactive) || 0, isemployee: parseInt(driver.isemployee) || 0,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this driver?')) return;
    try {
      const res = await apiClient.delete('/Drivers.php', { data: { driverid: id } });
      if (res.data.success) { alert('Driver deleted successfully!'); loadDrivers(); }
      else alert('Error: ' + res.data.message);
    } catch (err) { alert('Error deleting driver: ' + (err.response?.data?.message || err.message)); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.drivername.trim()) { alert('Driver name is required'); return; }
    try {
      const payload = { ...formData, companyid: session?.companyid, isactive: formData.isactive ? 1 : 0, isemployee: formData.isemployee ? 1 : 0 };
      const res = editData ? await apiClient.put('/Drivers.php', payload) : await apiClient.post('/Drivers.php', payload);
      if (res.data.success) {
        alert(editData ? 'Driver updated successfully!' : 'Driver created successfully!');
        setShowForm(false); setEditData(null); loadDrivers();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) { alert('Error saving driver: ' + (err.response?.data?.message || err.message)); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };

  const handleViewWorks = async (driver) => {
    setWorksDriver(driver);
    setShowWorks(true);
    setWorksLoading(true);
    setWorksError('');
    setWorksData([]);
    try {
      const res = await apiClient.get('/DriverWorks.php', { params: { driverid: driver.driverid } });
      if (res.data.success) setWorksData(res.data.data || []);
      else setWorksError(res.data.error || res.data.message || 'Failed to load works');
    } catch (err) {
      setWorksError(err.response?.data?.error || err.response?.data?.message || 'Failed to load works');
    } finally {
      setWorksLoading(false);
    }
  };

  const filteredDrivers = drivers.filter(driver => {
    const matchSearch = driver.drivername?.toLowerCase().includes(searchTerm.toLowerCase()) || driver.phone?.includes(searchTerm) || driver.phone1?.includes(searchTerm);
    const matchStatus = statusFilter === 'all' || (statusFilter === 'active' && parseInt(driver.isactive) === 1) || (statusFilter === 'inactive' && parseInt(driver.isactive) === 0);
    const matchType   = typeFilter   === 'all' || (typeFilter === 'hired' && driver.driverstatus === 'H') || (typeFilter === 'contract' && driver.driverstatus === 'C');
    return matchSearch && matchStatus && matchType;
  });

  const clearFilters = () => { setSearchTerm(''); setStatusFilter('all'); setTypeFilter('all'); };

  const inputCls = `w-full px-3.5 py-3 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)]`;
  const filterSelectCls = `px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-700 bg-white outline-none cursor-pointer transition-all duration-150 hover:border-[#0d1c6b] focus:border-[#0d1c6b]`;

  // ── no session ─────────────────────────────────────────────────────────
  if (!session?.companyid) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <User size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <User size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Drivers Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage driver information and records</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={handleNew}
            className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
            style={{ color: NAVY }}>
            <Plus size={17} /> New Driver
          </button>

          <button onClick={() => setShowHelp(true)} title="How this page works"
            className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg border-0 cursor-pointer transition-all duration-150 hover:bg-white/30"
            style={{ color: '#fff' }}>
            <HelpCircle size={20} />
          </button>
        </div>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by name or phone..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>

        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className={filterSelectCls}>
          <option value="all">All Types</option>
          <option value="hired">Hired</option>
          <option value="contract">Contract</option>
        </select>

        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={filterSelectCls}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        {(searchTerm || statusFilter !== 'all' || typeFilter !== 'all') && (
          <button onClick={clearFilters}
            className="px-4 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-50 hover:border-gray-300">
            Clear Filters
          </button>
        )}

        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          {filteredDrivers.length} {filteredDrivers.length === 1 ? 'driver' : 'drivers'}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">
            Loading drivers...
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200 gap-4">
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-semibold">
              <strong>Error:</strong> {error}
            </div>
            <button onClick={loadDrivers}
              className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
              Retry
            </button>
          </div>
        ) : filteredDrivers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <User size={48} className="text-gray-200 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-2">No drivers found</p>
            <p className="text-sm text-gray-400 mb-4">
              {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by adding your first driver'}
            </p>
            {!searchTerm && statusFilter === 'all' && typeFilter === 'all' && (
              <button onClick={handleNew}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
                style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                <Plus size={17} /> New Driver
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Driver Name', 'Address', 'Contact', 'Type', 'Status', 'Employee', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDrivers.map(driver => (
                  <tr key={driver.driverid}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <User size={15} className="text-[#667eea] flex-shrink-0" />
                        <span className="font-semibold text-gray-800">{driver.drivername}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      {driver.address1 && (
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <MapPin size={13} className="text-gray-400 flex-shrink-0" />
                          <span>{driver.address1}</span>
                        </div>
                      )}
                      {driver.address2 && (
                        <div className="text-[12px] text-gray-500 pl-5 mt-0.5">{driver.address2}</div>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-col gap-1">
                        {driver.phone && (
                          <div className="flex items-center gap-1.5 text-sm text-gray-600">
                            <Phone size={13} className="text-gray-400 flex-shrink-0" />{driver.phone}
                          </div>
                        )}
                        {driver.phone1 && (
                          <div className="flex items-center gap-1.5 text-sm text-gray-600">
                            <Phone size={13} className="text-gray-400 flex-shrink-0" />{driver.phone1}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border-2 ${
                        driver.driverstatus === 'H'
                          ? 'bg-blue-50 text-blue-700 border-blue-300'
                          : 'bg-amber-50 text-amber-700 border-amber-300'
                      }`}>
                        {driver.driverstatus === 'H' ? '🚗 Hired' : '📝 Contract'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        parseInt(driver.isactive) === 1
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-400'
                          : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {parseInt(driver.isactive) === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border-2 ${
                        parseInt(driver.isemployee) === 1
                          ? 'bg-blue-50 text-blue-700 border-blue-300'
                          : 'bg-gray-100 text-gray-500 border-gray-300'
                      }`}>
                        {parseInt(driver.isemployee) === 1 ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleViewWorks(driver)} title="Works"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-sky-100 border-sky-400 text-sky-700 transition-all duration-150 hover:bg-sky-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(3,105,161,0.3)]">
                          <Briefcase size={13} />
                        </button>
                        <button onClick={() => handleEdit(driver)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(driver.driverid)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── DRIVER FORM MODAL ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[560px] shadow-2xl border border-gray-200 overflow-hidden max-h-[90vh] flex flex-col"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <User size={18} />
                {editData ? 'Edit Driver' : 'New Driver'}
              </h3>
              <button onClick={() => setShowForm(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-7">

              {/* Basic Info */}
              <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wide px-3.5 py-2.5 bg-gray-50 border border-gray-200 border-l-4 rounded-r-lg mb-4"
                style={{ borderLeftColor: NAVY }}>
                Basic Information
              </div>

              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Driver Name <span className="text-red-500">*</span>
                </label>
                <input type="text" name="drivername" value={formData.drivername}
                  onChange={handleChange} required placeholder="Enter driver name"
                  className={inputCls} />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Address Line 1</label>
                  <input type="text" name="address1" value={formData.address1} onChange={handleChange} placeholder="Street address" className={inputCls} />
                </div>
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Address Line 2</label>
                  <input type="text" name="address2" value={formData.address2} onChange={handleChange} placeholder="City, area" className={inputCls} />
                </div>
              </div>

              <div className="mb-5">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Address Line 3</label>
                <input type="text" name="address3" value={formData.address3} onChange={handleChange} placeholder="State, country" className={inputCls} />
              </div>

              {/* Contact */}
              <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wide px-3.5 py-2.5 bg-gray-50 border border-gray-200 border-l-4 rounded-r-lg mb-4"
                style={{ borderLeftColor: NAVY }}>
                Contact Information
              </div>

              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Phone</label>
                  <input type="text" name="phone" value={formData.phone} onChange={handleChange} placeholder="+1234567890" className={inputCls} />
                </div>
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Phone 2</label>
                  <input type="text" name="phone1" value={formData.phone1} onChange={handleChange} placeholder="Alternate phone" className={inputCls} />
                </div>
              </div>

              {/* Type & Status */}
              <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wide px-3.5 py-2.5 bg-gray-50 border border-gray-200 border-l-4 rounded-r-lg mb-4"
                style={{ borderLeftColor: NAVY }}>
                Driver Type & Status
              </div>

              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Driver Type</label>
                <div className="flex gap-4">
                  {[{ val: 'H', label: '🚗 Hired' }, { val: 'C', label: '📝 Contract' }].map(({ val, label }) => (
                    <label key={val} className="flex items-center gap-2.5 cursor-pointer px-4 py-2.5 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150 select-none">
                      <input type="radio" name="driverstatus" value={val}
                        checked={formData.driverstatus === val} onChange={handleChange}
                        className="w-4 h-4 accent-[#0d1c6b]" />
                      <span className="text-sm font-semibold text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {[{ name: 'isactive', label: 'Active' }, { name: 'isemployee', label: 'Is Employee' }].map(({ name, label }) => (
                  <label key={name} className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                    <input type="checkbox" name={name} checked={formData[name] === 1} onChange={handleChange}
                      className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                    <span className="text-sm font-semibold text-gray-700">{label}</span>
                  </label>
                ))}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300">
                  Cancel
                </button>
                <button type="submit"
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)]"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {editData ? 'Update Driver' : 'Create Driver'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── WORKS MODAL ── */}
      {showWorks && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          onClick={() => setShowWorks(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[700px] shadow-2xl border border-gray-200 overflow-hidden max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <Briefcase size={18} /> Works — {worksDriver?.drivername}
              </h3>
              <button onClick={() => setShowWorks(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90">
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-6">
              {worksLoading ? (
                <div className="flex items-center justify-center py-16 text-gray-400 font-semibold">Loading works...</div>
              ) : worksError ? (
                <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-semibold">
                  {worksError}
                </div>
              ) : worksData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Briefcase size={40} className="text-gray-200 mb-3" />
                  <p className="text-gray-400 font-semibold">No consignments assigned to this driver yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        {['Consignment No', 'Route', 'Shipper / Consignee', 'Vehicle', 'Date', 'Status'].map(h => (
                          <th key={h} className="px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {worksData.map((w, i) => (
                        <tr key={w.ConsignmentID ?? i} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2.5 text-sm font-semibold text-gray-800">{w.ConsignmentNo}</td>
                          <td className="px-3 py-2.5 text-sm text-gray-600">{w.OriginCity} → {w.DestinationCity}</td>
                          <td className="px-3 py-2.5 text-sm text-gray-600">{w.ShipperName} / {w.ConsigneeName}</td>
                          <td className="px-3 py-2.5 text-sm text-gray-600">{w.VehicleNo}</td>
                          <td className="px-3 py-2.5 text-sm text-gray-600">{w.ConsignmentDate}</td>
                          <td className="px-3 py-2.5">
                            <span className="inline-flex px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 border-2 border-blue-300">
                              {w.Status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── HELP MODAL ── */}
      {showHelp && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[600px] shadow-2xl border border-gray-200 overflow-hidden max-h-[90vh] flex flex-col"
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <HelpCircle size={18} /> How to Use Drivers Master
              </h3>
              <button onClick={() => setShowHelp(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90">
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-7 space-y-5">
              {[
                { step: '1', title: 'Add a Driver', desc: 'Click "New Driver" at the top right. Fill in the driver name (required), address, and phone numbers.' },
                { step: '2', title: 'Choose Driver Type', desc: 'Select "Hired" for employed drivers or "Contract" for outsourced/freelance drivers.' },
                { step: '3', title: 'Set Status', desc: 'Check "Active" to make the driver available for assignment. Check "Is Employee" if they\'re on your payroll.' },
                { step: '4', title: 'Search & Filter', desc: 'Use the search box to find drivers by name or phone. Use the dropdowns to filter by type or status.' },
                { step: '5', title: 'Edit or Delete', desc: 'Use the icons in the Actions column. Note: a driver linked to existing consignments can\'t be deleted — set them Inactive instead.' },
                { step: '6', title: 'View Works', desc: 'Click the briefcase icon on a driver row to see their assigned consignments/trips.' },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex gap-4">
                  <div className="w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-bold text-white"
                    style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                    {step}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
                    <div className="text-sm text-gray-600">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end px-7 py-4 border-t border-gray-200 flex-shrink-0">
              <button onClick={() => setShowHelp(false)}
                className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
                style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default Drivers;